<template>
  <div :class="{ 'no-interact': source.includes('youtu') }">
    <div v-if="type === 'audio'" class="player-audio-card max-w-screen-lg mx-auto flex flex-col md:flex-row p-4 gap-4 rounded-lg bg-white dark:bg-neutral-600">
      <template v-if="$slots.default">
        <div class="inline-block w-32 h-32 border-2 border-neutral-500/25 rounded-lg shrink-0">
          <div v-if="AudioImg">
            <slot name="cover">
              <img :src="AudioImg" alt="" class="w-full h-full aspect-square object-cover" />
            </slot>
          </div>
        </div>
        <div class="grow flex flex-col">
          <slot></slot>
          <div class="grow"></div>
          <div class="rounded-lg">
            <audio ref="playerRef"></audio>
          </div>
        </div>
      </template>
      <div v-else class="grow">
        <audio ref="playerRef"></audio>
      </div>
    </div>
    <div v-else-if="type === 'video' && !source.includes('iframe')" class="relative bg-black aspect-video rounded-lg">
      <video
        v-show="!isBusy"
        ref="playerRef"
        crossorigin="anonymous"
        controls
        :poster="poster"
        class="w-full h-full"
      >
        <source :src="`https://${pullzone}/${videoId}/playlist.m3u8`" type="application/x-mpegURL">
        <track
          v-for="cap in enabledCaptions"
          kind="captions"
          :key="cap.srclang"
          :label="languageType[cap.srclang?.toLowerCase()]"
          :srclang="cap.srclang?.toLowerCase()"
          :src="`https://${pullzone}/${videoId}/captions/${cap.srclang}.vtt`"
        />
      </video>
      <div v-if="isBusy" class="absolute text-white inset-0 flex items-center justify-center">
        <div class="text-center">
          <UiLoader class="inline-block" />
          <div class="animate-pulse">Carregando</div>
        </div>
      </div>
    </div>
    <UiPdfViewer
      v-else-if="type === 'pdf' && source"
      ref="pdfViewerRef"
      :source="source"
      :allow-download="allowDownload"
      :watermark="watermark"
      :watermark-rules="watermarkRules"
      :is-class="isClassPdf"
    />
    <div v-else-if="type === 'externalLink' || (type === 'link' && linkSource.isEmbed)" class="flex text-center justify-center h-full dark:bg-black rounded-md overflow-hidden">
      <div v-if="!linkSource.isEmbed" class="box-border bg-white dark:bg-neutral-700 max-w-[465px] md:max-w-[730px] w-full shadow-2xl p-10 my-auto
          flex md:flex-row flex-col justify-center items-center">

        <div class="mx-auto md:hidden inline-block text-center py-2 max-w-80">
          <div class="inline-block text-xl lg:text-2xl text-neutral-800 dark:text-neutral-200 py-2">
            {{ $t('labels.accessLinkDescription') }}
          </div>
        </div>

        <UiCustomSvg class="h-64 w-full flex justify-center items-center">
          <ImgAccessLink  class=" max-w-64" />
        </UiCustomSvg>

        <div class="w-full flex flex-col justify-center items-center">
          <div class="mx-auto md:inline-block hidden text-center w-full md:max-w-80">
            <div class="inline-block text-xl md:text-2xl text-neutral-800 dark:text-neutral-200 py-2">
              {{ $t('labels.accessLinkDescription') }}
            </div>
          </div>

          <div class="mt-8 mx-auto">
            <div class="flex flex-wrap gap-4 justify-center">
              <UiButton
                type="primary"
                :class="[
                  'cursor-pointer rounded-lg py-3 text-lg px-16 lg:px-20',
                  sidebar.minimize ? 'md:px-16' : 'md:px-8'
                ]"
                tabindex="1"
                @click="handleLinkClick(source)">
                {{ $t('button.accessNow') }}
              </UiButton>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="grow">
        <iframe
          :src="linkSource.src"
          class="w-full h-full border-0"
          @load="() => onLoadFrame()"
        />
      </div>
    </div>
    <div
      v-else-if="type === 'link' && !source.includes('soundcloud')"
      ref="playerRef"
      :data-plyr-provider="source.includes('youtu') ? 'youtube': 'vimeo'"
      :data-plyr-embed-id="getVideoId"
      class="w-full h-full flex flex-col"
    />
    <iframe
      v-else-if="
        ['scorm', 'document', 'documento'].includes(type) || (type === 'link' && source.includes('soundcloud'))
        || (type === 'video' && source.includes('iframe'))
      "
      id="player-frame"
      :src="getDocumentLink"
      @load="() => onLoadFrame()"
      class="border-0 w-full h-full"
    />
    <div v-else-if="['image', 'imagem', 'img'].includes(type)" class="text-center">
      <img :src="source" alt="Player image" class="w-auto max-w-full mx-auto cursor-pointer hover:ring-2 ring-offset-2 transition-all rounded" @click="() => viewingImage = true" />
      <VueEasyLightbox
        :visible="viewingImage"
        :imgs="[source]"
        :index="0"
        @hide="() => viewingImage = false"
      />
    </div>
    <div v-else-if="type?.toLowerCase() === 'freecontent'" class="h-full overflow-auto">
      <FreeContentViewer
        :initialValue="freeContent"
        :mediaId="mediaId"
      />
    </div>
    <UiImageCarousel
      v-else-if="['carrossel', 'carousel'].includes(type)"
      :images="viewSlides"
      class="w-full h-full"
      @onUpdateProgress="updateCarrosselProgress"
    />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, computed, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import Hls from 'hls.js'
// @ts-ignore
import Plyr from 'plyr'
import 'plyr/dist/plyr.css'
import { midiaService } from '@/services/midias.service'
import { useSystemStore } from '@/stores'
import { languageType } from '@/common/util'
import { useStorage } from '@vueuse/core'

import UiCustomSvg from '@/components/ui/customSvg.vue'
import ImgAccessLink from '@/assets/art/accessLink.vue'

import playerConfig from './playerConfig'

import UiButton from '@/components/ui/button.vue'
import UiImageCarousel from '@/components/ui/imageCarousel/index.vue'
import UiPdfViewer from '@/components/ui/pdfViewer/index.vue'
import UiLoader from '@/components/ui/loader.vue'

import AudioImg from '@/assets/placeholder/audio.jpg'
import FreeContentViewer from './freeContent.vue'

import type { PlyrEvent } from 'plyr'

const { t } = useI18n()

const sidebar = useStorage('sideBar', {
  minimize: false,
  offScreen: false
})

const {
  source = '',
  slides = [],
  type = '',
  captions = [],
  progress = 0,
  videoId = '',
  chapters = [],
  pullzone = '',
  disableFastForward = false,
  allowScreenshot = false,
  videoInfo = null
} = defineProps<{
  mediaId?: string
  allowScreenshot?: boolean
  source: string
  type: string
  videoId?: string
  pullzone?: string
  progress?: number
  chapters?: any[]
  captions?: any[]
  allowDownload?: boolean
  watermark?: boolean
  watermarkRules?: {
    enabled?: boolean
    username?: boolean
    email?: boolean
    company?: boolean
    unit?: boolean
    department?: boolean
    position?: boolean
    dateTime?: boolean
    ip?: boolean
  }
  slides?: string[]
  disableFastForward?: boolean
  objData?: any
  isClassPdf?: boolean
  freeContent?: string
  videoInfo?: any
}>()

const emit = defineEmits(['onEnded', 'onUpdateProgress', 'onLoadFrame', 'onScreenshot'])
const linkSource = computed(() => {
  if (!source) return { src: '', isEmbed: false }

  const url = new URL(source)
  url.searchParams.delete('pembed')

  const isEmbed = source.includes('pembed=true')
  return {
    src: url.toString(),
    isEmbed
  }
})

const viewSlides = computed(() => {
  if (slides && slides.length > 0) return slides
  else return useSystemStore().playing?.slides || []
})

let hls: Hls = null as any
let player: Plyr = null as any

const isBusy = ref(false)
const viewingImage = ref(false)

const pdfViewerRef = ref()
const playerRef = ref()

// Armazena o progresso já percorrido pelo vídeo
let watchedTime = 0

const autoCaptions = ref([])
const autoChapters = ref([])
const doneLoading = ref(false)
const poster = ref('')

const enabledCaptions = computed(() => {
  if (captions.length > 0) return captions
  else return autoCaptions.value
})

const progressTrack = {
  blocked: false,
  value: 0,
  timeout: null
}

const fileExtension = computed(() => source?.split('.').at(-1) || '')

const getDocumentLink = computed(() => {
  if (['document', 'documento'].includes(type) && ['xls', 'xlsx', 'pptx', 'ppt', 'doc', 'docx', 'odp', 'ods', 'odt'].includes(fileExtension.value)) {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${source}`
  } else if (type === 'link' && source.includes('soundcloud')) {
    return `https://w.soundcloud.com/player/?url=${source}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true`
  }
  else return source
})

const getVideoId = computed(() => {
  if (source?.includes('youtu')) {
    let temp : any = null
    temp = source.includes('v=') ? source.split('=')[1] : source.split('/').at(-1)
    return temp.includes('&') ? temp.split('&')[0] : temp
  } else {
    return source.split('/').at(-1)
  }
})

watch(
  () => [pdfViewerRef.value?.page, pdfViewerRef.value?.pageCount],
  (nVal, oVal) => {
    if (!oVal[1] && !!nVal[1] && nVal[1] > 1 && progress > 0 && progress < 100) {
      pdfViewerRef.value?.jumpToPercent(progress)
    }
    if (pdfViewerRef.value?.pageCount) {
      const percent = Math.round((pdfViewerRef.value.page / pdfViewerRef.value.pageCount) * 100)
      percent > progress && updateProgress(percent, true)
    }
  }
)

const getVideoInfo = async () => {
  if (!videoId) return

  doneLoading.value = false

  try {
    const { data: videoData } = videoInfo ? { data: videoInfo } : await midiaService.getVideoInfo(videoId)

    autoCaptions.value = []
    autoChapters.value = []

    if (videoData) {
      autoChapters.value = videoData.chapters.map((chap: any) => ({
        start: chap.start,
        end: chap.end,
        title: chap.title
      }))

      autoCaptions.value = videoData.captions.map((cap: any) => ({
        label: cap.label,
        srclang: cap.srclang
      }))

      poster.value = `https://${pullzone}/${videoId}/${videoData.thumbnailFileName}`
    }
  } catch {
    //
  } finally {
    doneLoading.value = true
  }
}

const handleLinkClick = (source: any) => {
  window.open(source, '_blank');
}

const onLoadFrame = () => {
  if (type === 'scorm') emit('onLoadFrame')
}

const updateProgress = async (value: number, force?: boolean) => {
  if (!force) {
    if (progressTrack.blocked) return

    setTimeout(() => {
      progressTrack.blocked = false
    }, 10000)

    progressTrack.blocked = true
    progressTrack.value = value
  }

  emit('onUpdateProgress', value)
}

const updateCarrosselProgress = (value: number) => {
  if (value > progress) updateProgress(value, true)
}

const updateQuality = (newQuality) => {
  hls?.levels?.forEach((level, levelIndex) => {
    if (level?.height === newQuality) {
      hls.currentLevel = levelIndex
    }
  })
}

const initPlyr = async (config?: {}) => {
  if (player) player.destroy()

  playerConfig.autoplay = useSystemStore().options.autoplay
  playerConfig.volume = useSystemStore().options.volume

  const controls = ['play-large', 'restart', 'rewind', 'fast-forward', 'play', 'progress', 'current-time', 'duration', 'mute', 'volume', 'captions', 'settings', 'pip', 'airplays', 'fullscreen']
  const speed = {
    selected: 1,
    options: [0.5, 0.75, 1, 1.25, 1.5, 2]
  }

  if (disableFastForward) {
    controls.splice(controls.indexOf('fast-forward'), 1)
    speed.options = [0.5, 0.75, 1]
  }

  let ch : any[] = []
  if ((chapters).length > 0) {
    ch = chapters
  } else if (autoChapters.value.length > 0) {
    ch = autoChapters.value
  }

  player = new Plyr(playerRef.value, {
    ...playerConfig,
    ...config,
    controls,
    speed,
    keyboard: {
      global: true
    },
    settings: ['captions', 'quality', 'speed'],
    captions: {
      update: true,
      active: (!!captions?.length)
    },
    markers: {
      enabled: true,
      points: ch.map((el: any) => ({ time: el.start, label: el.title }))
    }
  } as any)

  if (['audio', 'video'].includes(type)) player.on('loadedmetadata', onLoadedMetadata)
  else player.on('ready', checkDurationRecursive)

  player.on('error', (event) => {
    console.error('Plyr error:', event)
  })

  const checkSeek = () => {
    if (disableFastForward && player.currentTime > (watchedTime + 1.5)) {
      player.currentTime = watchedTime
    }
  }

  // Caso o usuário "avance" manualmente
  player.on('seeking', checkSeek)
  player.on('seeked', checkSeek)

  player.on('timeupdate', onTimeUpdate)
  player.on('ended', onEnded)

  if (allowScreenshot) createButtonScreenshot()

  if (useSystemStore().options.autoplay) {
    if (progress && progress > 0 && progress < 100) {
      nextTick(() => {
        if (!player) return
        player.currentTime = (progress / 100) * player.duration
        watchedTime = player.currentTime
        player?.play()?.catch(() => {})
      })
    } else if (progress && progress === 100) {
      player?.play()?.catch(() => {})
    }
  } else if (progress && progress < 100 && player) {
    nextTick(() => {
      if (!player) return
      player.currentTime = (progress / 100) * player.duration
      watchedTime = player.currentTime
    })
  }

  return player
}


const onTimeUpdate = () => {
  if (!player) return

  if (!player.seeking && player.currentTime > watchedTime) {
    watchedTime = player.currentTime
  }

  let percent = player.currentTime && player.duration ? ((player.currentTime * 100) / player.duration) : 0

  if (type === 'video' && player.duration > 0) {
    const isNearEnd = player.currentTime >= (player.duration - 5)
    const alreadyCompleted = progressTrack.value === 100

    // Permite apenas finalizar o vídeo se o usuário realmente assistiu até a zona de buffer
    const hasReachedBuffer = watchedTime >= (player.duration - 6) // Dá 1s de margem para o watchedTime

    if (isNearEnd && !alreadyCompleted && (hasReachedBuffer || !disableFastForward)) {
      progressTrack.blocked = false
      progressTrack.value = 100
      percent = 100
      watchedTime = player.duration
      emit('onEnded')
    }
  }

  updateProgress(percent)
}

const onEnded = () => {
  if (progressTrack.value === 100) return

  updateProgress(100, true)
  emit('onEnded')
}

const checkDurationRecursive = (event: PlyrEvent) => {
  if (player && player.duration === 0)
    setTimeout(() => checkDurationRecursive(event), 50)
  else if (player)
    return onLoadedMetadata(event)
}

const onLoadedMetadata = (event: PlyrEvent) => {
  if (progress > 0) {
    player.currentTime = (progress / 100) * event.detail.plyr.duration
    watchedTime = player.currentTime
  }
}

/**
 * Inicializa o HLS (HTTP Live Streaming) para vídeos
 */
const initHls = async () => {
  if (!source)
    return

  if (!Hls.isSupported())
    throw t('errors.notSupportStreaming')

  if (hls) hls.destroy()

  const videoInfo = getVideoInfo()

  hls = new Hls()
  hls.loadSource(`//${source}`)
  hls.attachMedia(playerRef.value)

  hls.on(Hls.Events.ERROR, (event, data) => {
    if (data.fatal) {
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          console.error('HLS fatal network error encountered, try to recover')
          hls.startLoad()
          break
        case Hls.ErrorTypes.MEDIA_ERROR:
          console.error('HLS fatal media error encountered, try to recover')
          hls.recoverMediaError()
          break
        default:
          console.error('HLS fatal error encountered, cannot recover', data)
          hls.destroy()
          break
      }
    }
  })

  // Lê o manifest da playlist m3u8 (Importante para extrari informações para o Plyr)
  const readManifest = new Promise((resolve) => {
    hls.on(Hls.Events.MANIFEST_PARSED, async () => {
      const availableQualities = hls?.levels?.map((l) => l.height)

      playerConfig.quality = {
        default: availableQualities.find((el) => el === 1080) || availableQualities[availableQualities.length - 1],
        options: availableQualities,
        forced: true,
        onChange: (e) => {
          updateQuality(e)
        }
      }

      resolve(true)
    })
  })

  isBusy.value = true
  await Promise.all([videoInfo, readManifest])

  isBusy.value = false
  await initPlyr(playerConfig)

  player?.on('languagechange', () => {
    setTimeout(() => (hls.subtitleTrack = player.currentTrack), 50)
  })
}

const initSoundCloudPlayer = () => {
  const SC = (window as any).SC
  const widget = SC.Widget(document.getElementById('player-frame'))

  widget.bind(SC.Widget.Events.PLAY_PROGRESS, ({ relativePosition }) => {
    relativePosition > 0 && updateProgress(Math.round((relativePosition * 100)))
  })
  widget.bind(SC.Widget.Events.FINISH, () => {
    updateProgress(100, true)
    emit('onEnded')
  })
}

const loadMediaLink = async () => {
  source.includes('soundcloud') ? initSoundCloudPlayer() : initPlyr()
}

const loadMediaVideo = async () => {
  Hls.isSupported() && !source.includes('iframe') ? initHls() : initPlyr()
}

const loadMediaAudio = async () => {
  await initPlyr()

  player.source = {
    type: 'audio',
    title: '',
    sources: [
      {
        src: source,
        type: 'audio/mp3'
      }
    ]
  }
}

const captureFrame = () => {
  let md = (player as any)?.media
  if (!md) return

  const width = md.videoWidth
  const height = md.videoHeight
  const canvas = Object.assign(document.createElement('canvas'), { width, height })
  const canvasCtx = canvas.getContext('2d')
  const img = new Image()

  canvasCtx?.drawImage(md, 0, 0, width, height)

  img.setAttribute('crossOrigin', 'anonymous')
  img.onload = () => {
    const imageURl = img.src.replace(/^data:image\/[^;]+/, 'data:application/octet-stream')
    emit('onScreenshot', imageURl)
  }

  img.src = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream')
}

const createButtonScreenshot = () => {
  if (type !== 'video') return

  setTimeout(() => {
    const menu = document.querySelector('.plyr__controls__item.plyr__menu')
    if (!menu) return

    const btn = `
      <button class="plyr__controls__item plyr__control" type="button" data-plyr="capture">
        <svg role="presentation" focusable="false">
          <path d="M9.098,7.566c0.758,0,1.408,0.27,1.946,0.809c0.539,0.538,0.809,1.188,0.809,1.946c0,0.758-0.27,1.406-0.809,1.946
          c-0.538,0.539-1.188,0.81-1.946,0.81c-0.759,0-1.408-0.271-1.947-0.81c-0.539-0.54-0.808-1.188-0.808-1.946
          c0-0.759,0.27-1.408,0.808-1.946C7.689,7.835,8.339,7.566,9.098,7.566z M14.862,3.471c0.59,0,1.093,0.241,1.511,0.723
          C16.79,4.676,17,5.258,17,5.939v8.646c0,0.682-0.21,1.264-0.627,1.747c-0.418,0.481-0.921,0.724-1.511,0.724H3.107
          c-0.59,0-1.095-0.242-1.511-0.724c-0.417-0.483-0.627-1.065-0.627-1.747V5.939c0-0.682,0.21-1.264,0.627-1.746
          c0.417-0.482,0.921-0.723,1.511-0.723h1.87l0.426-1.313c0.105-0.315,0.299-0.587,0.58-0.815C6.264,1.114,6.552,1,6.846,1h4.274
          c0.297,0,0.584,0.114,0.866,0.342c0.28,0.228,0.475,0.5,0.58,0.815l0.424,1.313H14.862L14.862,3.471z M9.098,14.606
          c1.179,0,2.188-0.419,3.026-1.258c0.84-0.839,1.258-1.847,1.258-3.027c0-1.18-0.418-2.188-1.258-3.027
          c-0.839-0.839-1.848-1.259-3.026-1.259c-1.18,0-2.189,0.42-3.028,1.259c-0.838,0.839-1.258,1.847-1.258,3.027
          c0,1.181,0.42,2.188,1.258,3.027C6.909,14.188,7.917,14.606,9.098,14.606z"/>
        </svg>
      </button>
    `
    menu.insertAdjacentHTML('beforebegin', btn)

    document
      .querySelector('button[data-plyr="capture"]')
      ?.addEventListener('click', captureFrame)
  }, 1000)
}

onMounted(async () => {
  switch (type) {
    case 'link':
      await loadMediaLink()
      break;
    case 'video':
      await loadMediaVideo()
      break;
    case 'audio':
      await loadMediaAudio()
      break;
  }
})

onBeforeUnmount(() => {
  try {
    if (player) player.destroy()
    player = null as any
    if (hls) hls.destroy()
    hls = null as any
  } catch (error) {
    //
  }
})
</script>

<style lang="scss">
:root {
  --plyr-color-main: #875fc1;
}

.plyr__tooltip {
  /* max-width: none !important; */
}

.no-interact {
  .plyr__video-embed iframe {
    pointer-events: none;
  }
}

.plyr {
  border-radius: 0.5rem;
}

.player-audio-card {
  .plyr--audio {
    .plyr__menu__container {
      bottom: auto !important;
      top: calc(100% + 10px) !important;
      margin-bottom: 0 !important;
      z-index: 10 !important;

      &::after {
        top: auto !important;
        bottom: 100% !important;
        border-top-color: transparent !important;
        border-bottom-color: var(--plyr-menu-background, rgba(255, 255, 255, 0.9)) !important;
      }
    }
  }
}
</style>
